import { AccountInfo, IPublicClientApplication } from "@azure/msal-browser";
import { useAccount, useMsal } from "@azure/msal-react";
import axios, { InternalAxiosRequestConfig } from "axios";
import { loginRequest } from "../../authConfig";

interface RequestInterceptorProps {
  children: React.ReactElement;
}

/**
 * Builds an Axios request interceptor to add authentication tokens to requests.
 *
 * @param instance - MSAL instance for acquiring tokens
 * @param account - Active user account
 * @param stage - Application stage (e.g., local, development, production)
 * @returns Axios request interceptor function
 */
export function buildAuthInterceptor(
  instance: IPublicClientApplication,
  account: AccountInfo | null,
  stage: string | undefined,
) {
  return async function intercept(
    config: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> {
    if (stage !== "local") {
      if (!account) {
        throw Error("No active account! Verify a user has been signed in.");
      }
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      });
      const bearer = `Bearer ${response.accessToken}`;
      if (config && config.headers)
        (config.headers as Record<string, string>).Authorization = bearer;
    }
    return config;
  };
}

/**
 * Axios request interceptor that adds authentication tokens to outgoing requests.
 *
 * @param props - The component props
 * @param props.children - Child components to render
 */
const RequestInterceptor: React.FC<RequestInterceptorProps> = ({
  children,
}: RequestInterceptorProps) => {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0]);
  const interceptor = buildAuthInterceptor(instance, account, process.env.REACT_APP_STAGE);
  axios.interceptors.request.use(interceptor);
  return <>{children}</>;
};

RequestInterceptor.displayName = "RequestInterceptor";

export default RequestInterceptor;
